import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ISubtask } from '../../../../models/subtask.model';
import { AppState } from '../../../../state/configureStore';
import { deleteSubtask } from '../../../../state/subtask';
import { TaskState } from '../../../../state/task';
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  actionGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
    borderRadius: 999,
    border: '1px solid rgba(148, 163, 184, 0.18)',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.9) 100%)',
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
  },
  actionButton: {
    minHeight: 40,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 14,
    textTransform: 'none',
    fontWeight: 700,
    letterSpacing: 0,
    boxShadow: 'none',
    transition:
      'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 10px 18px rgba(15, 23, 42, 0.1)',
    },
  },
  deleteButton: {
    color: '#b91c1c',
    borderColor: 'rgba(239, 68, 68, 0.28)',
    backgroundColor: 'rgba(254, 242, 242, 0.88)',
    '&:hover': {
      borderColor: 'rgba(220, 38, 38, 0.34)',
      backgroundColor: '#ffffff',
    },
  },
  editButton: {
    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 52%, #60a5fa 100%)',
    boxShadow: '0 12px 24px rgba(37, 99, 235, 0.24)',
    '&:hover': {
      background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 52%, #3b82f6 100%)',
      boxShadow: '0 14px 28px rgba(37, 99, 235, 0.3)',
    },
  },
}));

interface ISubtaskActionMenuProps {
  subtask: ISubtask;
  setEditSubtaskId: (subtaskId: number) => void;
}

const SubtaskActionMenu: React.FC<ISubtaskActionMenuProps> = (props) => {
  const classes = useStyles();
  const { subtask, setEditSubtaskId } = props;
  const dispatch = useDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { item: task } = useSelector<AppState, TaskState>((state) => state.task);

  const deleteSMSubtask = (subtask: ISubtask) => {
    const { id } = task;
    dispatch(deleteSubtask({ id: subtask.id, smTaskId: id }));
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Button
          variant={'outlined'}
          size="small"
          color="default"
          startIcon={<DeleteIcon />}
          onClick={() => setOpenDeleteDialog(true)}
          className={`${classes.actionButton} ${classes.deleteButton}`}
        >
          Delete
        </Button>
        <Button
          variant={'contained'}
          color="primary"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => {
            if (subtask.id) {
              setEditSubtaskId(subtask.id);
            }
          }}
          className={`${classes.actionButton} ${classes.editButton}`}
        >
          Edit
        </Button>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: 18,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          style={{
            padding: '24px 24px 12px',
            background: 'linear-gradient(135deg, #ef5350, #c62828)',
          }}
        >
          <Box display="flex" alignItems="center">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                marginRight: 14,
                color: '#fff5f5',
                background: 'rgba(255, 255, 255, 0.14)',
              }}
            >
              <DeleteIcon />
            </Box>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#ffffff' }}>
                Confirm Delete
              </Typography>
              <Typography
                variant="body2"
                style={{ color: 'rgba(255, 255, 255, 0.82)', marginTop: 4 }}
              >
                This action cannot be undone.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            style={{ color: '#4b5563', lineHeight: 1.7, marginBottom: 0 }}
          >
            Are you sure you want to delete this subtask? Once deleted, the related
            information will be removed permanently.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px 24px', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="default"
            style={{ borderRadius: 10, padding: '8px 18px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteSMSubtask(subtask)}
            color="secondary"
            variant="contained"
            style={{ borderRadius: 10, padding: '8px 18px', boxShadow: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubtaskActionMenu;
